import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { getErrorMessage } from "@vega-api/custom/utils/errorHandling";
import { useToast } from "@vega-core/components/Toast/useToast";
import { m } from "@vega-core/paraglide/messages";

type CoInvestErrorToastOptions = {
  defaultTitle?: string;
  defaultMessage?: string;
  statusCodeMessages?: Record<number, { title?: string; message: string }>;
};

/**
 * Co-invest-scoped error toast that always displays translated messages.
 * Raw backend error strings are logged to console in dev mode but never shown to users.
 * Supports per-status-code message overrides via `statusCodeMessages` option.
 */
export const useCoInvestErrorToast = () => {
  const showToast = useToast();

  const showErrorToast = (
    error: ErrorType<unknown>,
    options: CoInvestErrorToastOptions = {},
  ): { statusCode: number | undefined } => {
    const { statusCode, description: rawDescription } = getErrorMessage(error);

    if (rawDescription) {
      console.error("[CoInvest API Error]", rawDescription);
    }

    const statusOverride =
      statusCode != null ? options.statusCodeMessages?.[statusCode] : undefined;

    showToast({
      title: statusOverride?.title ?? options.defaultTitle ?? m.status_error(),
      description:
        statusOverride?.message ??
        options.defaultMessage ??
        m.common_error_somethingWentWrong(),
      variant: "destructive",
    });

    return { statusCode };
  };

  return showErrorToast;
};
